import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type DashboardMetrics,
  formatCurrency,
  getDashboardMetrics,
  type MetricsPeriod,
} from '@/api/metrics';

/**
 * Formatted values derived from raw metrics.
 */
export interface FormattedValues {
  totalSuppliers: string;
  totalClients: string;
  periodPaymentVolume: string;
  periodPaymentFiles: string;
  periodPayments: string;
  totalProgramSpend: string;
  inProgramSpend: string;
  revenueGenerated: string;
  inProgramSuppliersCount: string;
  outOfProgramSuppliersCount: string;
  optimizedSuppliersCount: string;
  digitalAdoptionRate: string;
  clientRebateShare: string;
  effectiveRebate: string;
  interchangeCreated: string;
  clientRebatePercentage: string;
  beneficiaryApproved: string;
  beneficiaryDenied: string;
  beneficiaryAwaitingApproval: string;
  beneficiaryAchCount: string;
  beneficiaryCheckCount: string;
  beneficiaryDigitalWalletCount: string;
}

/**
 * Dashboard data hook interface with metrics and formatted values.
 */
export interface DashboardData {
  metrics: DashboardMetrics | null;
  formattedValues: FormattedValues | null;
  isLoading: boolean;
  error: string | null;
}

function formatMetrics(metrics: DashboardMetrics): FormattedValues {
  return {
    totalSuppliers: metrics.totalSuppliers.toString(),
    totalClients: metrics.totalClients.toString(),
    periodPaymentVolume: formatCurrency(metrics.periodPaymentVolume),
    periodPaymentFiles: metrics.periodPaymentFiles.toString(),
    periodPayments: metrics.periodPayments.toString(),
    totalProgramSpend: formatCurrency(metrics.totalProgramSpend),
    inProgramSpend: formatCurrency(metrics.inProgramSpend),
    revenueGenerated: formatCurrency(metrics.revenueGenerated),
    inProgramSuppliersCount: metrics.inProgramSuppliers.toString(),
    outOfProgramSuppliersCount: metrics.outOfProgramSuppliers.toString(),
    optimizedSuppliersCount: metrics.optimizedSuppliers.toString(),
    digitalAdoptionRate: `${Math.round(metrics.digitalAdoptionRate)}%`,
    clientRebateShare: `${metrics.clientRebateShare}%`,
    effectiveRebate: formatCurrency(metrics.effectiveRebate),
    interchangeCreated: formatCurrency(metrics.interchangeCreated),
    clientRebatePercentage: metrics.clientRebatePercentage || 'N/A',
    beneficiaryApproved: (metrics.beneficiaryApproved ?? 0).toString(),
    beneficiaryDenied: (metrics.beneficiaryDenied ?? 0).toString(),
    beneficiaryAwaitingApproval: (metrics.beneficiaryAwaitingApproval ?? 0).toString(),
    beneficiaryAchCount: (metrics.beneficiaryAchCount ?? 0).toString(),
    beneficiaryCheckCount: (metrics.beneficiaryCheckCount ?? 0).toString(),
    beneficiaryDigitalWalletCount: (metrics.beneficiaryDigitalWalletCount ?? 0).toString(),
  };
}

export const useDashboardData = (period: MetricsPeriod = 'this_month') => {
  // Cache of fetched results keyed by period — survives re-renders
  const cacheRef = useRef<
    Map<MetricsPeriod, { metrics: DashboardMetrics; formattedValues: FormattedValues }>
  >(new Map());

  const [data, setData] = useState<DashboardData>({
    metrics: null,
    formattedValues: null,
    isLoading: true,
    error: null,
  });

  const fetchDashboardData = useCallback(async () => {
    // If we already have cached data for this period, show it immediately (no loading flash)
    const cached = cacheRef.current.get(period);
    if (cached) {
      setData({
        metrics: cached.metrics,
        formattedValues: cached.formattedValues,
        isLoading: false,
        error: null,
      });
      return;
    }

    try {
      // Only show loading skeleton on initial load (no data yet).
      // On period switches, keep showing the previous data to avoid stutter.
      setData((prev) => ({
        ...prev,
        isLoading: prev.formattedValues === null,
        error: null,
      }));

      const metrics = await getDashboardMetrics(period);
      const formattedValues = formatMetrics(metrics);

      // Store in cache
      cacheRef.current.set(period, { metrics, formattedValues });

      setData({
        metrics,
        formattedValues,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setData((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load dashboard data',
      }));
    }
  }, [period]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const refetch = useCallback(() => {
    // Clear cache for current period so it re-fetches fresh
    cacheRef.current.delete(period);
    fetchDashboardData();
  }, [fetchDashboardData, period]);

  return {
    ...data,
    refetch,
  };
};
